import type { CompanyStatus } from './schemas.ts';

/** Who is reading the label. */
export type StatusAudience = 'admin' | 'vendor';

/**
 * Status wording, split by who is looking at it.
 *
 * One map served both the admin badge and the contractor's own sidebar, which
 * is why the register found the label "wrong in both directions at once": a
 * single string cannot say both "this is on your desk" and "this is on theirs".
 * The DB enum is untouched — this is presentation only, and no `submitted` /
 * `resubmitted` status is added.
 *
 * `expired` is renamed for BOTH audiences: the objection is that "Not
 * Compliant" collides with the per-answer reject verdict, and that collision
 * exists on the vendor's screen just as much as on the admin's.
 *
 * Lives in @l2u/shared rather than @l2u/web-shared because apps/api renders
 * these words too (the exported compliance pack). apps/api is a Cloudflare
 * Worker and cannot import a browser package.
 */
export const COMPANY_STATUS_LABELS: Record<CompanyStatus, string> = {
  invited: 'Invited',
  in_progress: 'In progress',
  review: 'Awaiting your review',
  changes: 'Changes requested — with them',
  compliant: 'Compliant',
  expired: 'Document lapsed',
  deactivated: 'Deactivated',
};

/** The same statuses as the vendor sees them. */
export const COMPANY_STATUS_LABELS_VENDOR: Record<CompanyStatus, string> = {
  invited: 'Invited',
  in_progress: 'In progress',
  review: 'Submitted — awaiting review',
  changes: 'Changes requested — your action',
  compliant: 'Compliant',
  expired: 'Document lapsed',
  deactivated: 'Deactivated',
};

/**
 * THE accessor. Defaults to the admin wording because the admin app has far
 * more call sites and every one of them predates the split — a default of
 * 'vendor' would silently reword the whole back office.
 */
export function companyStatusLabel(
  status: CompanyStatus,
  audience: StatusAudience = 'admin',
): string {
  return audience === 'vendor'
    ? COMPANY_STATUS_LABELS_VENDOR[status]
    : COMPANY_STATUS_LABELS[status];
}

/** Statuses in lifecycle order — drives the order of filter dropdown options. */
export const COMPANY_STATUSES: CompanyStatus[] = [
  'invited',
  'in_progress',
  'review',
  'changes',
  'compliant',
  'expired',
  'deactivated',
];
